import React from 'react';

export default function ContactPage() {
  return (
    <div className="bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-[#4AB8D8] hover:text-[#F8A356]">Home</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">Contact & Location</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact & Location</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Visit us, call us, or send us a message - we're here to help with all your laboratory testing needs
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Contact Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 text-center hover:shadow-xl transition-shadow">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4AB8D8]/20 rounded-full mb-4">
              <svg className="w-8 h-8 text-[#4AB8D8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Phone</h3>
            <a href="tel:9842309599" className="text-[#4AB8D8] hover:text-[#F8A356] font-semibold text-lg block mb-1">
              (984) 230-9599
            </a>
            <p className="text-gray-600 text-sm">Mon-Fri: 8AM-5PM | Sat-Sun: 8AM-2PM</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8 text-center hover:shadow-xl transition-shadow">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F8A356]/20 rounded-full mb-4">
              <svg className="w-8 h-8 text-[#F8A356]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
            <a href="mailto:info@EmergeNCDiagnostics.com" className="text-[#4AB8D8] hover:text-[#F8A356] font-semibold block mb-1 break-all">
              info@EmergeNCDiagnostics.com
            </a>
            <p className="text-gray-600 text-sm">We respond within 24 hours</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8 text-center hover:shadow-xl transition-shadow">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4AB8D8]/20 rounded-full mb-4">
              <svg className="w-8 h-8 text-[#4AB8D8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Fax</h3>
            <p className="text-gray-700 font-semibold text-lg mb-1">(984) 231-7779</p>
            <p className="text-gray-600 text-sm">For medical records & forms</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Location & Hours */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Location</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2 flex items-center gap-2">
                  <svg className="w-6 h-6 text-[#4AB8D8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Address
                </h3>
                <p className="text-gray-700 ml-8">
                  EmergeNC Diagnostics, LLC<br />
                  4801 Glenwood Ave Suite 200 PMB 333<br />
                  Raleigh, NC 27612
                </p>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=4801+Glenwood+Ave+Suite+200+PMB+333+Raleigh+NC+27612"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4AB8D8] hover:text-[#F8A356] font-semibold ml-8 inline-block mt-2"
                >
                  Get Directions →
                </a>
              </div>

              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-6 h-6 text-[#F8A356]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Business Hours
                </h3>
                <div className="ml-8 space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span className="font-medium">Monday - Friday:</span>
                    <span>8:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Saturday:</span>
                    <span>8:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Sunday:</span>
                    <span>8:00 AM - 2:00 PM</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">
                    * Walk-ins welcome, but appointments are recommended to minimize wait times
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Parking Information</h3>
                <p className="text-gray-700 text-sm">
                  Free parking available on-site. Accessible parking spaces located near the main entrance.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
            <form className="space-y-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-semibold text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
                >
                  <option value="">-- Select Subject --</option>
                  <option value="appointment">Appointment Inquiry</option>
                  <option value="results">Test Results Question</option>
                  <option value="billing">Billing Question</option>
                  <option value="services">Service Information</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
                  placeholder="How can we help you?"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#4AB8D8] text-white px-8 py-3 rounded-lg hover:bg-[#3A9AB8] transition-colors font-semibold"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>

        {/* Google Maps Embed */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-12">
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Find Us on the Map</h2>
          </div>
          <div className="relative w-full h-96">
            <iframe
              title="EmergeNC Diagnostics Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3235.8!2d-78.6547!3d35.8142!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac5f1c9e7e8e8f%3A0x1234567890abcdef!2s4801%20Glenwood%20Ave%20Suite%20200%2C%20Raleigh%2C%20NC%2027612!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0"
            />
          </div>
          <div className="p-4 bg-gray-50 text-center">
            <a 
              href="https://www.google.com/maps/search/?api=1&query=4801+Glenwood+Ave+Suite+200+PMB+333+Raleigh+NC+27612"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#4AB8D8] hover:text-[#F8A356] font-semibold"
            >
              Open in Google Maps →
            </a>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="font-semibold text-red-900 text-lg mb-1">Medical Emergencies</h3>
              <p className="text-red-800">
                For medical emergencies, please call 911 or visit your nearest emergency room. 
                EmergeNC Diagnostics provides laboratory testing services and is not an emergency care facility.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
